/**
 * Thai messages — the DEFAULT locale and the SOURCE OF TRUTH for keys.
 * `en.ts` is typed `Record<MessageKey, string>`, so a missing or extra key there is
 * a compile error. Same shape as apps/driver/src/i18n/messages (ADR 0289 D7).
 *
 * Keys are flat and dotted. No placeholder engine: compose with template strings so
 * every value stays a whole, translatable sentence.
 */
export const th = {
  // ── หน้าแรกเข้า ────────────────────────────────────────
  "app.name": "CCA Customer",
  "landing.title": "ดูรอบรถรับส่ง",
  "landing.subtitle": "สแกน QR ที่ป้ายรถหรือในรถ",
  "landing.scan": "สแกน QR",
  "landing.enterCode": "กรอกรหัส",

  // ── สแกน ──────────────────────────────────────────────
  "scan.title": "สแกน QR",
  "scan.aim": "เล็ง QR ให้อยู่ในกรอบ",
  "scan.torchOn": "ไฟฉาย",
  "scan.torchOff": "ปิดไฟฉาย",
  "scan.flip": "สลับกล้อง",
  "scan.codePlaceholder": "ABCD-2345",
  "scan.submit": "ยืนยัน",
  "scan.hint": "สแกน QR ที่ป้ายรถหรือในรถ",
  "scan.opening": "กำลังเปิดกล้อง...",
  "scan.checking": "กำลังตรวจสอบ...",
  "scan.useCode": "กรอกรหัสแทน",
  "scan.codeTitle": "กรอกรหัสจากป้าย",
  "scan.codeSub": "รหัส 8 ตัวที่พิมพ์อยู่ใต้ QR",
  "scan.codeEnter": "เข้าใช้งาน",
  "scan.retry": "ลองใหม่",
  "scan.openSettings": "เปิดตั้งค่าแอป",
  "scan.close": "ปิด",
  "scan.permissionHelpApp": "เปิดสิทธิ์กล้องให้แอปในหน้าตั้งค่า แล้วกลับมากดลองใหม่",
  "scan.permissionHelpWeb": "เปิดสิทธิ์กล้องให้เว็บนี้ในตั้งค่าเบราว์เซอร์ แล้วกดลองใหม่",
  "err.QR_INVALID": "QR ไม่ถูกต้อง — ตรวจว่าสแกนถูกป้ายหรือไม่",
  "err.QR_REVOKED": "QR นี้ถูกยกเลิกแล้ว — ขอรหัสใหม่จากผู้ดูแล",
  "err.QR_EXPIRED": "QR หมดอายุแล้ว — ขอรหัสใหม่จากผู้ดูแล",
  "err.QR_NOT_STARTED": "QR นี้ยังไม่ถึงวันเริ่มใช้ — ลองใหม่ในวันที่กำหนด",
  "err.RATE_LIMITED": "ลองบ่อยเกินไป — รอสักครู่แล้วลองใหม่",
  "err.LIST_FULL": "เก็บได้สูงสุด 6 ใบ — ลบใบที่ไม่ใช้ออกก่อน",
  "err.NETWORK": "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง",
  "err.NO_PERMISSION": "ไม่ได้รับอนุญาตให้ใช้กล้อง",
  "err.NO_CAMERA": "ไม่พบกล้องบนอุปกรณ์นี้",
  "err.UNKNOWN": "เปิดกล้องไม่สำเร็จ ลองใหม่อีกครั้ง",
  "scan.back": "ย้อนกลับ",

  // ── รายการรถที่วิ่งอยู่ ──────────────────────────────────
  "trips.plate": "ทะเบียนรถ",
  "trips.driver": "คนขับ",
  "trips.none": "ขณะนี้ไม่มีรถให้บริการ",
  "trips.offline": "เชื่อมต่อไม่ได้ — ข้อมูลอาจไม่ใช่ล่าสุด",
  "trips.expired": "QR หมดอายุแล้ว",
  "trips.notStarted": "QR ยังไม่ถึงวันเริ่มใช้",

  // ── แถบบน / เมนู ──────────────────────────────────────
  "nav.addPortal": "สแกน QR เพิ่ม",
  "nav.back": "ย้อนกลับ",
  "nav.portalList": "รายการ",
  "nav.tabList": "รายการ",
  "nav.tabSettings": "ตั้งค่า",
  "settings.groupGeneral": "ทั่วไป",
  "settings.language": "ภาษา",
  "settings.managePortals": "จัดการรายการ",
  "settings.portalActive": "กำลังเปิดอยู่",
  "settings.removeOne": "ลบรายการนี้",
  "settings.removeOneBody": "จะหายจากเครื่องนี้ · สแกน QR เดิมใหม่ได้ตลอด",
  "settings.removeAll": "ลบทั้งหมด",
  "settings.removeAllBody": "ลบ QR ทุกใบออกจากเครื่องนี้ · สแกนใหม่ได้ตลอด",
  "settings.confirmRemove": "ลบ",
  "settings.cancel": "ยกเลิก",
  "common.loading": "กำลังโหลด",
  "nav.language": "ภาษา",

  // ── รายละเอียดเที่ยว ──────────────────────────────────
  "trip.title": "รายละเอียดเที่ยว",
  "trip.stops": "จุดจอด",
  "trip.km": "กม.",
  "trip.minutes": "นาที",
  "trip.depart": "ออก",
  "trip.arriveApprox": "ถึงโดยประมาณ",
  "trip.unscheduled": "ยังไม่กำหนด",
  "trip.track": "ติดตามรถบนแผนที่",
  "trip.crewLater": "ข้อมูลรถและคนขับจะแสดง 15 นาทีก่อนเวลาออก",
  "trip.noStops": "ยังไม่มีข้อมูลจุดจอดของเที่ยวนี้",
  "trip.cancelledNote": "เที่ยวนี้ถูกยกเลิกแล้ว — จะไม่มีรถออก",
  "trip.notFound": "ไม่พบเที่ยวนี้",
  "trip.backToList": "กลับไปหน้ารายการ",
  "trip.sessionOver": "QR หมดอายุแล้ว — สแกนใหม่อีกครั้ง",
  "trip.point": "จุด",

  // ── สถานะเที่ยว ───────────────────────────────────────
  "state.running": "กำลังวิ่ง",
  "state.upcoming": "รอออก",
  "state.completed": "ถึงแล้ว",
  "state.cancelled": "ยกเลิก",

  // ── ไทม์ไลน์จุดจอด ────────────────────────────────────
  "stop.planNote": "เที่ยวนี้ไม่มีบันทึกเวลาเข้าจุดจอด — เวลาที่แสดงคือเวลาตามแผน",
  "stop.origin": "ต้นทาง",
  "stop.destination": "ปลายทาง",
  "stop.depot": "อู่รถ",
  "stop.next": "จุดถัดไป",
  "stop.nearest": "ใกล้คุณที่สุด",
  "stop.skipped": "ข้ามจุดนี้",
  "stop.passed": "ผ่านแล้ว",
  "stop.nth": "จุดที่",
  "stop.from": "จาก",
  "stop.dwell": "จอดรับ",
  "stop.metres": "ม.",

  // ── แผนที่ ────────────────────────────────────────────
  "map.title": "ติดตามรถ",
  "map.vehicle": "รถ",
  "map.plate": "รถทะเบียน",
  "map.driver": "คนขับ",
  "map.you": "รถของคุณ",
  "map.goToBus": "ไปที่รถ",
  "map.goToMe": "ไปที่ตำแหน่งของฉัน",
  "map.loading": "กำลังโหลดตำแหน่งรถ...",
  "map.notYet": "รถจะเริ่มแสดงตำแหน่ง 15 นาทีก่อนเวลาออก",
  "map.noSignal": "ยังไม่ได้รับสัญญาณ GPS จากรถคันนี้",
  "map.dead": "เซสชันหมดอายุ — สแกน QR ใหม่อีกครั้ง",
  "map.justNow": "เมื่อครู่",
  "map.fitAll": "ดูทั้งเส้นทาง",
  "map.updated": "อัปเดตล่าสุด",
  "map.onTime": "ตามกำหนด",
  "map.arrived": "ถึงแล้ว",
  "map.notReached": "ยังไม่ถึง",
  "map.minutesAgo": "นาทีที่แล้ว",
  "map.hoursAgo": "ชม.ที่แล้ว",
  "map.daysAgo": "วันที่แล้ว",

  // ── ภาษา ──────────────────────────────────────────────
  "lang.th": "ไทย",
  "lang.en": "English",
} as const;

export type MessageKey = keyof typeof th;
